/**
 * Accessible mode: persistent visible labels (WCAG 3.3.2). A text-like field that
 * has no visible label (no shown <label for>, wrapping <label> or aria-labelledby
 * target with visible text; hidden and sr-only ones do not count) gets one
 * inserted before it, taken from aria-label, else the placeholder. The inserted
 * node is aria-hidden: the control already has its accessible name, so a screen
 * reader hears it once. Only runs while data-a11y-mode is on.
 */

export const LABEL_CLASS = 'a11y-mode-label';
const OWNER_ATTR = 'data-a11y-label-for';

const SKIPPED_INPUT_TYPES = new Set(['hidden', 'checkbox', 'radio', 'submit', 'button', 'reset', 'image', 'range', 'file', 'color']);
const FIELD_SELECTOR = 'input, textarea, select';

const VISUALLY_HIDDEN_CLASSES = ['sr-only', 'visually-hidden'];

/** False when `el` or an ancestor is hidden, display:none, visibility:hidden or visually hidden (sr-only). */
function isShown(el: Element): boolean {
  const view = el.ownerDocument.defaultView;
  for (let n: Element | null = el; n; n = n.parentElement) {
    if (n.hasAttribute('hidden')) return false;
    if (VISUALLY_HIDDEN_CLASSES.some((c) => n!.classList.contains(c))) return false;
    const cs = view?.getComputedStyle(n);
    if (!cs) continue;
    if (cs.display === 'none') return false;
    if (n === el && (cs.visibility === 'hidden' || cs.visibility === 'collapse')) return false;
    if (/^rect\(\s*0(px)?[\s,]+0(px)?[\s,]+0(px)?[\s,]+0(px)?\s*\)$/.test(cs.clip)) return false;
    if (cs.position === 'absolute' && cs.width === '1px' && cs.height === '1px' && cs.overflow === 'hidden') return false;
  }
  return true;
}

/** Text a sighted user can see in `node`, ignoring hidden parts and the field itself. */
function visibleText(node: Node, field: Element): string {
  let out = '';
  node.childNodes.forEach((c) => {
    if (c.nodeType === Node.TEXT_NODE) out += c.textContent ?? '';
    else if (c instanceof Element && c !== field && !c.matches(FIELD_SELECTOR) && isShown(c)) out += visibleText(c, field);
  });
  return out.trim();
}

function shownWithText(label: Element | null, field: Element): boolean {
  return !!label && isShown(label) && !!visibleText(label, field);
}

function hasVisibleLabel(el: HTMLElement): boolean {
  const doc = el.ownerDocument;
  if (shownWithText(el.closest('label'), el)) return true;
  if (el.id && Array.from(doc.querySelectorAll(`label[for="${CSS.escape(el.id)}"]`)).some((l) => shownWithText(l, el))) return true;
  const by = el.getAttribute('aria-labelledby');
  return !!by && by.split(/\s+/).some((id) => shownWithText(doc.getElementById(id), el));
}

function labelText(el: HTMLElement): string {
  return (el.getAttribute('aria-label') || el.getAttribute('placeholder') || '').trim();
}

function eligible(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLInputElement && SKIPPED_INPUT_TYPES.has(el.type)) return false;
  return !!labelText(el) && !hasVisibleLabel(el);
}

// Also watch the attributes that decide the label text or whether one is needed.
const OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  attributes: true,
  // hidden/class: an associated label that is hidden or made sr-only no longer counts as visible
  attributeFilter: ['aria-label', 'placeholder', 'id', 'for', 'aria-labelledby', 'type', 'hidden', 'class'],
};

let counter = 0;
function owned(label: Element): string | null {
  return label.getAttribute(OWNER_ATTR);
}

function sweep(root: HTMLElement): void {
  // drop labels whose field is gone or no longer eligible
  root.querySelectorAll(`.${LABEL_CLASS}`).forEach((l) => {
    const next = l.nextElementSibling;
    if (!next || next.getAttribute('data-a11y-label-id') !== owned(l) || !eligible(next)) l.remove();
  });
  root.querySelectorAll(FIELD_SELECTOR).forEach((f) => {
    if (!eligible(f)) return;
    const prev = f.previousElementSibling;
    const id = f.getAttribute('data-a11y-label-id') ?? `a11y-l-${++counter}`;
    f.setAttribute('data-a11y-label-id', id);
    const text = labelText(f);
    if (prev && prev.classList.contains(LABEL_CLASS) && owned(prev) === id) {
      if (prev.textContent !== text) prev.textContent = text;
      return;
    }
    const span = f.ownerDocument.createElement('span');
    span.className = LABEL_CLASS;
    span.setAttribute('aria-hidden', 'true');
    span.setAttribute(OWNER_ATTR, id);
    span.textContent = text;
    f.parentNode?.insertBefore(span, f);
  });
}

/** Label every eligible field under `root` now and as the DOM changes; returns a cleanup that removes what it added. */
export function applyPersistentLabels(root: HTMLElement): () => void {
  sweep(root);
  const MO = root.ownerDocument.defaultView?.MutationObserver;
  let scheduled = false;
  let disposed = false;
  const observer = MO
    ? new MO(() => {
        if (scheduled) return;
        scheduled = true;
        queueMicrotask(() => {
          scheduled = false;
          if (disposed) return;
          observer?.disconnect();
          sweep(root);
          observer?.observe(root, OBSERVE_OPTIONS);
        });
      })
    : null;
  observer?.observe(root, OBSERVE_OPTIONS);
  return () => {
    disposed = true;
    observer?.disconnect();
    root.querySelectorAll(`.${LABEL_CLASS}`).forEach((l) => l.remove());
    root.querySelectorAll('[data-a11y-label-id]').forEach((f) => f.removeAttribute('data-a11y-label-id'));
  };
}
